import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SidebarSectionToggle } from "@/components/layout/sidebar/sidebar-section-toggle";
import { useRunCommand } from "@/lib/commands/CommandProvider";
import { useDmListData } from "./dm-list/use-dm-list-data";
import { DmRow } from "./dm-list/DmRow";
import { GroupDmRow } from "./dm-list/GroupDmRow";
import { MemberRow } from "./dm-list/MemberRow";
import { useDmActions } from "./dm-list/use-dm-actions";
import type { WorkspaceInfo } from "./dm-list/dm-display-filters";

interface DMListProps {
  workspaceId: string | null;
  workspaceName?: string;
  currentUserId: string;
  selectedChannel: string | null;
  onSelectChannel: (id: string | null) => void;
  isUnread?: (channelId: string) => boolean;
  unreadOnly?: boolean;
  recentOnly?: boolean;
  /** Workspaces the current user belongs to (for shared-workspace tooltips in Home) */
  myWorkspaces?: WorkspaceInfo[];
  /** Called when user clicks "Show all" to switch from recent to all */
  onShowAll?: () => void;
}

export function DMList({
  workspaceId,
  workspaceName = "Workspace",
  currentUserId,
  selectedChannel,
  onSelectChannel,
  isUnread,
  unreadOnly,
  recentOnly,
  myWorkspaces,
  onShowAll,
}: DMListProps) {
  const data = useDmListData({
    workspaceId,
    currentUserId,
    unreadOnly,
    recentOnly,
    isUnread,
  });
  const {
    selfDm,
    visibleDirectDms,
    visibleGroupDms,
    members,
    visibleMembersList,
    dmByUserId,
    collaboratorIdSet,
    membershipByChannelId,
    inviteByChannelId,
    displayWsDms,
    displayCollaboratorDms,
    displayWsGroupDms,
    displayGuestDms,
    displayHomeSharedDms,
    displayHomePersonalDms,
    displayHomeSharedGroupDms,
    displayHomePersonalGroupDms,
  } = data;

  // Stable DmActions for memoized DM/Member/GroupDm rows. The hook
  // funnels live deps (onSelectChannel, selectedChannel, etc.) through
  // a ref so the returned object's identity never changes —
  // *.memo bails out cleanly on Sidebar re-renders.
  const dmActions = useDmActions({
    onSelectChannel,
    selectedChannel,
    membershipByChannelId,
    dmByUserId,
  });

  const [wsOpen, setWsOpen] = useState(true);
  const [collaboratorsOpen, setCollaboratorsOpen] = useState(true);
  const [guestsOpen, setGuestsOpen] = useState(true);
  const [homeSharedOpen, setHomeSharedOpen] = useState(true);
  const [homePersonalOpen, setHomePersonalOpen] = useState(true);
  const [memberSearch, setMemberSearch] = useState("");
  const peopleSearch = memberSearch.trim().toLowerCase();

  const filteredMembers = useMemo(
    () =>
      peopleSearch
        ? visibleMembersList.filter((u) =>
            u.display_name.toLowerCase().includes(peopleSearch),
          )
        : visibleMembersList,
    [peopleSearch, visibleMembersList],
  );
  const filteredWorkspaceDms = useMemo(
    () =>
      peopleSearch
        ? displayWsDms.filter((dm) =>
            dm.displayName.toLowerCase().includes(peopleSearch),
          )
        : displayWsDms,
    [displayWsDms, peopleSearch],
  );

  const conversationOnlyFilter = unreadOnly || recentOnly;
  const workspacePeopleCount = displayWsDms.length + visibleMembersList.length;
  const workspacePeopleRows = useMemo(() => {
    const rows = [
      ...filteredWorkspaceDms.map((dm) => ({
        kind: "dm" as const,
        id: dm.userId ?? dm.channelId,
        label: dm.displayName,
        dm,
      })),
      ...filteredMembers.map((user) => ({
        kind: "member" as const,
        id: user.id,
        label: user.display_name,
        user,
      })),
    ];
    return rows.sort(
      (a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id),
    );
  }, [filteredWorkspaceDms, filteredMembers]);
  const wsItemCount =
    (conversationOnlyFilter
      ? displayWsDms.length
      : workspacePeopleRows.length) + displayWsGroupDms.length;

  // Empty state only when there's genuinely nothing to show. Use the
  // hidden-FILTERED counts (the raw DM query now includes hidden rows, so a
  // raw count would suppress the empty state for someone who hid everything).
  if (
    members.length === 0 &&
    visibleDirectDms.length === 0 &&
    visibleGroupDms.length === 0 &&
    !selfDm
  ) {
    return (
      <div className="pb-4">
        <DmListEmptyState />
      </div>
    );
  }

  const renderDm = (dm: (typeof displayWsDms)[number], key = dm.channelId) => {
    const invite = inviteByChannelId.get(dm.channelId);
    const inviteBadge = dm.isGuest ? invite?.invited_email || null : null;
    const inviteMethod: "email" | "link" | null = dm.isGuest
      ? invite?.invited_email
        ? "email"
        : "link"
      : null;
    return (
      <DmRow
        key={key}
        dm={dm}
        isSelected={selectedChannel === dm.channelId}
        isUnread={isUnread?.(dm.channelId) ?? false}
        isCollaborator={!!dm.userId && collaboratorIdSet.has(dm.userId)}
        actions={dmActions}
        myWorkspaces={!workspaceId ? myWorkspaces : undefined}
        inviteBadge={inviteBadge}
        inviteMethod={inviteMethod}
      />
    );
  };

  const renderGroupDm = (channel: { id: string; name: string }) => (
    <GroupDmRow
      key={channel.id}
      channel={channel}
      isSelected={selectedChannel === channel.id}
      actions={dmActions}
    />
  );

  const renderMember = (
    user: NonNullable<(typeof members)[number]>,
    key = user.id,
  ) => {
    const dmChId = dmByUserId.get(user.id);
    return (
      <MemberRow
        key={key}
        user={user}
        dmChannelId={dmChId}
        isSelected={!!dmChId && selectedChannel === dmChId}
        isUnread={!!dmChId && (isUnread?.(dmChId) ?? false)}
        actions={dmActions}
      />
    );
  };

  return (
    <div className="space-y-0.5 pb-4">
      {/* Self-DM ("Notes") — pinned at the top of the DM list in every scope.
          Hidden only in the strict unread-only filter (a read row is noise
          there); shows in the default + recent views. */}
      {selfDm && !unreadOnly && renderDm(selfDm, "self")}

      {/* Workspace members section */}
      {workspaceId && (wsItemCount > 0 || !unreadOnly) && (
        <>
          <SidebarSectionToggle
            label={`${workspaceName} members`}
            open={wsOpen}
            onToggle={() => setWsOpen((v) => !v)}
          />
          {wsOpen && (
            <>
              {!conversationOnlyFilter && workspacePeopleCount > 5 && (
                <input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search people..."
                  className="w-full bg-secondary rounded px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground/50 outline-none mb-1"
                />
              )}
              {conversationOnlyFilter
                ? displayWsDms.map((dm) => renderDm(dm))
                : workspacePeopleRows.map((row) =>
                    row.kind === "dm"
                      ? renderDm(row.dm, `person:${row.id}`)
                      : renderMember(row.user, `person:${row.id}`),
                  )}
              {displayWsGroupDms.map(renderGroupDm)}
              {wsItemCount === 0 && (
                <p className="text-muted-foreground/30 text-[11px] px-2 py-0.5">
                  No conversations
                </p>
              )}
            </>
          )}
        </>
      )}

      {/* Collaborators section */}
      {workspaceId && displayCollaboratorDms.length > 0 && (
        <>
          <SidebarSectionToggle
            label="Collaborators"
            open={collaboratorsOpen}
            onToggle={() => setCollaboratorsOpen((v) => !v)}
          />
          {collaboratorsOpen && (
            <>{displayCollaboratorDms.map((dm) => renderDm(dm))}</>
          )}
        </>
      )}

      {/* Personal DMs removed from workspace scope. The server query
          (dmsByWorkspaceAndUserWithMembers) filters to workspace members.
          Zero's shared client MemorySource may retain cached rows from
          Home scope — don't render them here. Personal DMs belong in
          Home scope only (rendered separately below). */}

      {/* Invite DMs section (workspace view) */}
      {workspaceId && displayGuestDms.length > 0 && (
        <>
          <div className="mt-1">
            <SidebarSectionToggle
              label="Invite DMs"
              open={guestsOpen}
              onToggle={() => setGuestsOpen((v) => !v)}
            />
          </div>
          {guestsOpen && <>{displayGuestDms.map((dm) => renderDm(dm))}</>}
        </>
      )}

      {/* Home view: 3-section grouping */}
      {!workspaceId && (
        <>
          {(displayHomeSharedDms.length > 0 ||
            displayHomeSharedGroupDms.length > 0) && (
            <>
              <SidebarSectionToggle
                label="Shared workspace"
                open={homeSharedOpen}
                onToggle={() => setHomeSharedOpen((v) => !v)}
              />
              {homeSharedOpen && (
                <>
                  {displayHomeSharedDms.map((dm) => renderDm(dm))}
                  {displayHomeSharedGroupDms.map(renderGroupDm)}
                </>
              )}
            </>
          )}

          {(displayHomePersonalDms.length > 0 ||
            displayHomePersonalGroupDms.length > 0) && (
            <>
              <div className="mt-1">
                <SidebarSectionToggle
                  label="Personal"
                  open={homePersonalOpen}
                  onToggle={() => setHomePersonalOpen((v) => !v)}
                />
              </div>
              {homePersonalOpen && (
                <>
                  {displayHomePersonalDms.map((dm) => renderDm(dm))}
                  {displayHomePersonalGroupDms.map(renderGroupDm)}
                </>
              )}
            </>
          )}

          {displayGuestDms.length > 0 && (
            <>
              <div className="mt-1">
                <SidebarSectionToggle
                  label="Invite DMs"
                  open={guestsOpen}
                  onToggle={() => setGuestsOpen((v) => !v)}
                />
              </div>
              {guestsOpen && <>{displayGuestDms.map((dm) => renderDm(dm))}</>}
            </>
          )}
        </>
      )}

      {/* Show all link when in recent mode */}
      {recentOnly && onShowAll && (
        <Button
          type="button"
          variant="link"
          onClick={onShowAll}
          className="h-auto px-2 py-1 text-[11px] text-muted-foreground/50"
        >
          Show all
        </Button>
      )}
    </div>
  );
}

/**
 * Empty-state copy + CTA for the DM section. Wired through the
 * existing `sidebar.newDirectMessage` command so the keyboard shortcut
 * (⌘+Shift+K) and the button share one handler — no parallel paths to
 * keep in sync.
 */
function DmListEmptyState() {
  const runCommand = useRunCommand();
  return (
    <EmptyState
      layout="inset"
      title="No conversations yet"
      action={
        <Button
          variant="outline"
          size="tiny"
          className="w-full"
          onClick={() => runCommand("sidebar.newDirectMessage")}
        >
          New message
        </Button>
      }
    />
  );
}
